/* eslint-disable eslint-comments/no-restricted-disable */
/* eslint-disable react/no-array-index-key */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './allocation_scheduler.module.scss';

interface Event {
  id: string;
  title: string;
  startTime: number;
  duration: number;
  color: string;
  gateKey: string;
  disableDrag?: boolean;
  disableResize?: boolean;
  isResize?: boolean
}

interface GateRow {
  id: string;
  text: string;
  label: string;
}

interface SchedulerProps {
  columnsData: GateRow[];
  rawEventsData: any[];
  disableDrag: boolean;
  disableResize: boolean;
  currentTimeIndicator: boolean;
  groupId: string;
  dateCurrent: Date;
  onItemClick: (
    // eslint-disable-next-line no-unused-vars
    event: any,
    // eslint-disable-next-line no-unused-vars
    e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent<HTMLDivElement>
  ) => void;
  onDragEnd: (
    // eslint-disable-next-line no-unused-vars
    event: any,
  ) => void;
  displayTimeHide: boolean;
}

const HOUR_WIDTH = 160;
const ROW_HEIGHT = 50;
let HOURS = Array.from({ length: 24 }, (_, i) => i);
const QUARTER_HOUR_SNAP_PX = HOUR_WIDTH / 4;
let HOURS_IN_DISPLAY = 24;

const normalizeDate = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

export default function Scheduler({ columnsData, rawEventsData = [], disableDrag = false, disableResize = false,
  currentTimeIndicator = true, groupId, dateCurrent = new Date(), onItemClick, onDragEnd, displayTimeHide = true }: SchedulerProps) {
  const [events, setEvents] = useState<Event[]>([]);
  const [gateRows, setGateRows] = useState<GateRow[]>([]);
  const [ghostEvent, setGhostEvent] = useState<Event | null>(null);

  const schedulerRef = useRef<HTMLDivElement>(null);
  const currentDragIdRef = useRef<string | null>(null);
  const ghostEventRef = useRef<Event | null>(null);

  const MAX_ROWS = gateRows.length;

  const getRowIndex = useCallback((gateKey: string) => gateRows.findIndex(row => row.id === gateKey), [gateRows]);

  useEffect(() => {
    setGateRows([...new Map(columnsData.map(item => [item.id, item])).values()]);
  }, [columnsData]);

  useEffect(() => {
    const selectedTimestamp = normalizeDate(dateCurrent);

    const filtered = rawEventsData?.length > 0 ? rawEventsData.filter(item => columnsData.some(col => col.id === item?.[groupId])
      && normalizeDate(new Date(item.dateStart)) === selectedTimestamp) : [];

    const newEvents = filtered.map((item) => {
      const start = new Date(item.dateStart);
      const end = new Date(item.dateEnd);
      return {
        ...item,
        id: String(item.id),
        title: item.displayText,
        startTime: start.getHours() + start.getMinutes() / 60,
        duration: (end.getTime() - start.getTime()) / 3600000,
        color: item.backgroundColor,
        gateKey: item?.[groupId]
      };
    });
    setEvents(newEvents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawEventsData, dateCurrent]);

  const getDynamicHours = useCallback(() => {
    const selectedTimestamp = normalizeDate(dateCurrent);
    const dayEndTimestamp = selectedTimestamp + 24 * 60 * 60 * 1000;

    const maxEndTimestamp = rawEventsData?.length > 0 ? rawEventsData?.reduce((maxTime, item) => {
      const eventStart = new Date(item.dateStart).getTime();
      const eventEnd = new Date(item.dateEnd).getTime();

      if (eventStart < dayEndTimestamp && eventEnd > selectedTimestamp) {
        return Math.max(maxTime, eventEnd);
      }
      return maxTime;
    }, 0) : [];

    if (maxEndTimestamp > dayEndTimestamp) {
      const extraHours = Math.ceil((maxEndTimestamp - dayEndTimestamp) / (60 * 60 * 1000));
      return 24 + extraHours;
    }
    return 24;
  }, [rawEventsData, dateCurrent]);

  const dynamicHoursInDisplay = getDynamicHours();
  HOURS = Array.from({ length: dynamicHoursInDisplay }, (_, i) => i);
  HOURS_IN_DISPLAY = dynamicHoursInDisplay;

  const formatTime = (hour: number) => {
    const totalMinutes = Math.round(hour * 60);
    const minutesOfDay = totalMinutes % (24 * 60);
    const h = Math.floor(minutesOfDay / 60);
    const m = (minutesOfDay % 60).toString().padStart(2, '0');
    const period = h < 12 ? 'AM' : 'PM';
    const formattedHour = h % 12 || 12;

    return `${formattedHour}:${m} ${period}`;
  };

  const onDragStart = useCallback((ev: Event, e: React.MouseEvent, isResize = false) => {
    e.preventDefault();
    if (isResize) e.stopPropagation();

    const container = schedulerRef.current;
    if (!container) return;

    currentDragIdRef.current = ev.id;
    setGhostEvent(null);

    const startX = e.clientX;
    const startY = e.clientY;
    const initialX = ev.startTime * HOUR_WIDTH;
    const initialY = getRowIndex(ev.gateKey) * ROW_HEIGHT;
    const initialScrollLeft = container.scrollLeft;

    const startDur = ev.duration;

    const onMouseMove = (move: MouseEvent) => {
      if (!schedulerRef.current) return;
      const EDGE = 80;
      const SCROLL = 20;
      const { left, right } = schedulerRef.current.getBoundingClientRect();

      if (move.clientX > right - EDGE) schedulerRef.current.scrollLeft += SCROLL;
      else if (move.clientX < left + EDGE) schedulerRef.current.scrollLeft -= SCROLL;

      let newStart = ev.startTime;
      let newGateKey = ev.gateKey;
      let newDur = ev.duration;

      const scrollDelta = schedulerRef.current.scrollLeft - initialScrollLeft;
      if (!isResize) {
        const newX = initialX + (move.clientX - startX) + scrollDelta;
        const newY = initialY + (move.clientY - startY);

        const constrainedX = Math.max(0, Math.min(newX, dynamicHoursInDisplay * HOUR_WIDTH - ev.duration * HOUR_WIDTH));
        const constrainedY = Math.max(0, Math.min(newY, MAX_ROWS * ROW_HEIGHT - ROW_HEIGHT));

        newStart = Math.round(constrainedX / QUARTER_HOUR_SNAP_PX) * QUARTER_HOUR_SNAP_PX / HOUR_WIDTH;
        const newRow = Math.min(MAX_ROWS - 1, Math.round(constrainedY / ROW_HEIGHT));
        newGateKey = gateRows[newRow]?.id || ev.gateKey;
      } else {
        const delta = ((move.clientX - startX) + scrollDelta) / HOUR_WIDTH;
        newDur = Math.max(0.25, Math.round((startDur + delta) * 4) / 4);
        const maxDuration = dynamicHoursInDisplay - ev.startTime;
        newDur = Math.min(newDur, maxDuration);
      }

      if (currentDragIdRef.current === ev.id) {
        const updated: Event = {
          ...ev,
          startTime: newStart,
          duration: newDur,
          gateKey: newGateKey,
          [groupId]: newGateKey,
          isResize: !!isResize
        };
        ghostEventRef.current = updated;
        setGhostEvent(updated);
      }
    };

    const onMouseUp = () => {
      const final = ghostEventRef.current;
      if (final?.id === currentDragIdRef.current) {
        const newStartTime = new Date(dateCurrent);
        newStartTime.setHours(Math.floor(final.startTime));
        newStartTime.setMinutes(Math.round((final.startTime % 1) * 60));

        const newEndTime = new Date(newStartTime.getTime() + final.duration * 60 * 60 * 1000);

        const updated = {
          ...final,
          dateStart: newStartTime.toISOString(),
          dateEnd: newEndTime.toISOString()
        };
        if (onDragEnd) onDragEnd(updated);
        setEvents(prev => prev.map(item => (item.id === updated.id ? updated : item)));
      }
      setGhostEvent(null);
      ghostEventRef.current = null;
      currentDragIdRef.current = null;

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [dynamicHoursInDisplay, gateRows, groupId, MAX_ROWS, onDragEnd, dateCurrent, getRowIndex]);

  const currentTime = new Date().getHours() + new Date().getMinutes() / 60;

  return (
    <div className={styles.scheduler}>
      <div className={styles.scheduler_wrapper} ref={schedulerRef}>
        <div className={styles.scheduler_grid}>
          <div className={styles.scheduler_header} style={{ height: ROW_HEIGHT }}>
            <div className={styles.row_labels} />
            {HOURS.map(hour => (
              <div key={hour} className={styles.hour_cell} style={{ width: HOUR_WIDTH }}>
                {formatTime(hour)}
              </div>
            ))}
          </div>

          <div className={styles.scheduler_rows}>
            <div className={styles.row_labels}>
              {gateRows.map(row => (
                <div key={row.id} className={styles.row_label} style={{ height: ROW_HEIGHT }}>
                  {row.text}
                </div>
              ))}
            </div>

            <div className={styles.timeline}>
              {/* Grid Lines */}
              {HOURS.map(hour => (
                <div key={hour} className={styles.grid_vertical_line} style={{ left: hour * HOUR_WIDTH }} />
              ))}
              {gateRows.map((_, i) => (
                <div key={i} className={styles.grid_horizontal_line} style={{ top: i * ROW_HEIGHT }} />
              ))}
              {Array.from({ length: HOURS_IN_DISPLAY * 4 }).map((_, i) => (
                <div key={i} className={styles.grid_vertical_line} style={{ left: i * QUARTER_HOUR_SNAP_PX }} />
              ))}

              {/* Ghost Preview */}
              {!disableDrag && ghostEvent && (
                <div
                  className={styles.ghost_event}
                  style={{
                    top: getRowIndex(ghostEvent.gateKey) * ROW_HEIGHT + 4,
                    left: ghostEvent.startTime * HOUR_WIDTH,
                    width: ghostEvent.duration * HOUR_WIDTH,
                    height: ROW_HEIGHT - 8
                  }}
                />
              )}

              {/* Events */}
              {events.map(event => (
                <div
                  key={`${event.id}-${event.startTime}-${event.gateKey}`}
                  className={`${styles.event} text-truncate ${(disableDrag || event?.disableDrag) ? styles.not_draggable : ''}`}
                  style={{
                    top: getRowIndex(event.gateKey) * ROW_HEIGHT + 4,
                    left: event.startTime * HOUR_WIDTH,
                    width: event.duration * HOUR_WIDTH,
                    height: ROW_HEIGHT - 8,
                    background: event.color
                  }}
                  onMouseDown={e => ((!event?.disableDrag && !disableDrag) ? onDragStart(event, e) : {})}
                  tabIndex={0}
                  role="button"
                  onClick={e => onItemClick && onItemClick(event, e)}
                  onKeyPress={e => onItemClick && onItemClick(event, e)}
                >
                  <div className={styles.event_content}>
                    <div className={styles.event_title}>{event.title}</div>
                    {displayTimeHide && (
                      <div className={styles.event_time}>
                        {formatTime(event.startTime)} - {formatTime(event.startTime + event.duration)}
                      </div>
                    )}
                  </div>
                  {(!disableResize || event?.disableResize) && (
                    <div
                      className={styles.event_resize_handle}
                      onMouseDown={e => ((!disableResize || event?.disableResize) && onDragStart(event, e, true))}
                      tabIndex={0}
                      role="button"
                    />
                  )}
                </div>
              ))}

              {/* Current Time Indicator */}
              {currentTimeIndicator && (
                <div className={styles.current_time_indicator} style={{ left: currentTime * HOUR_WIDTH }}>
                  <div className={styles.now_dot} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
